import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Button, Input } from '../components/ui';
import { api, ApiError, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-xl border border-cream-border bg-cream-surface p-6 shadow-sm">
        <img src="/logo-300.webp" alt="GauTattva Organics" className="mx-auto mb-4 h-24 w-auto" />
        <h1 className="mb-5 text-center font-heading text-2xl font-semibold text-forest-900">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export function Login() {
  const { status, login } = useAuth();
  const nav = useNavigate();
  const from = (useLocation().state as { from?: string } | null)?.from ?? '/';
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (status === 'signed-in') return <Navigate to={from} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(identifier.trim(), password);
      nav(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 429 ? 'Too many attempts. Please wait a few minutes and try again.' : errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Shell title="Admin sign in">
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Input label="Email" type="email" autoComplete="username" required value={identifier} onChange={(e) => setIdentifier(e.target.value)} />
        <Input label="Password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        {error && <p role="alert" className="text-sm text-danger">{error}</p>}
        <Button type="submit" loading={busy} className="w-full" disabled={!identifier || !password}>Sign in</Button>
      </form>
    </Shell>
  );
}

/** Invite / password-reset link: token arrives in the URL fragment (#token=…), never sent to servers. */
export function AcceptInvite() {
  const nav = useNavigate();
  // Read once: after success the token is removed from the address bar, and re-reading it would show "Invalid link".
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== password;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password.length < 12 || mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await api('/admin/auth/accept-invite', { method: 'POST', body: { token, password } });
      history.replaceState(null, '', window.location.pathname); // drop the token from the address bar
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  if (done) return <Shell title="Password set"><p className="mb-4 text-center text-sm">You can now sign in.</p><Button className="w-full" onClick={() => nav('/login')}>Go to sign in</Button></Shell>;
  if (!token) return <Shell title="Invalid link"><p className="text-center text-sm text-ink-muted">This link is incomplete. Open the full link you were sent, or ask an administrator for a new one.</p></Shell>;
  return (
    <Shell title="Set your password">
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Input label="New password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} hint="At least 12 characters. A short phrase is easiest to remember." error={password && password.length < 12 ? 'Use at least 12 characters' : undefined} />
        <Input label="Confirm password" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} error={mismatch ? "Passwords don't match" : undefined} />
        {error && <p role="alert" className="text-sm text-danger">{error}</p>}
        <Button type="submit" loading={busy} className="w-full" disabled={password.length < 12 || password !== confirm}>Set password</Button>
      </form>
    </Shell>
  );
}
